import type { ComponentRef, Injector, TemplateRef, Type } from '@angular/core';

import type { DialogRef } from './dialog-ref';

/**
 * BINDING CONTRACT - EHS_EMG_FE/docs/COMMON_COMPONENTS.md section 3.
 * Types only. Nothing here may be renamed, dropped, or made required.
 */

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type DialogPosition = 'center' | 'top' | 'right' | 'left' | 'bottom';
export type DialogAction = 'save' | 'apply' | 'reset' | 'cancel' | 'close' | 'backdrop' | 'esc';

export interface DialogResult<R = unknown> {
  action: DialogAction;
  data?: R;
}

export interface DialogConfig<TData = unknown> {
  title?: string;
  titleAccent?: string;
  subtitle?: string;

  size?: DialogSize;
  width?: string;
  height?: string;
  maxWidth?: string;

  position?: DialogPosition;
  offset?: { top?: string; left?: string; right?: string; bottom?: string };

  /** Forwarded to DIALOG_DATA and to the content's setValues(). */
  data?: TData;
  /** Pushed onto the content instance with ComponentRef.setInput(). */
  inputs?: Record<string, unknown>;
  /** Auto-subscribed output() / EventEmitter members, unsubscribed on destroy. */
  outputs?: Record<string, (value: never) => void>;

  showSave?: boolean;
  saveLabel?: string;
  showApply?: boolean;
  applyLabel?: string;
  showReset?: boolean;
  resetLabel?: string;
  showCancel?: boolean;
  cancelLabel?: string;
  showHeaderClose?: boolean;

  /** Total footer takeover. */
  footerTemplate?: TemplateRef<{ ref: DialogRef<unknown> }>;
  footerAlign?: 'start' | 'end' | 'between';

  disableSaveWhenInvalid?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  closeOnSave?: boolean;

  panelClass?: string | string[];
  ariaLabel?: string;
}

/**
 * Every member optional - the frame feature-detects each one, so a
 * display-only component needs none of them.
 */
export interface DialogContent<TData = unknown, TPayload = unknown> {
  setValues?(data: TData): void;
  getPayload?(): TPayload;
  isFormValid?(): boolean;
  resetForm?(): void;
  /** Apply pressed; the dialog stays open. */
  onApply?(): void;
  /**
   * Save pressed while `closeOnSave: false` - the keep-open mode.
   *
   * The frame has already set `busy`, so the content owns the round trip and must call
   * `ref.close(...)` itself on success or `ref.setBusy(false)` on failure. Falls back to
   * {@link onApply} when absent, so a component that treats Save and Apply identically
   * needs only one hook.
   */
  onSave?(): void;
  /** Dirty-check veto. */
  canClose?(): boolean | Promise<boolean>;
}

/** Internal: one entry of the DialogService stack. Not part of the public contract. */
export interface DialogInstance<TData = unknown, R = unknown> {
  readonly id: string;
  readonly component: Type<unknown>;
  readonly config: DialogConfig<TData>;
  readonly ref: DialogRef<R>;
  readonly injector: Injector;
}

/** Options accepted by DialogService.confirm(). Structurally accepts ActionConfig['confirm']. */
export interface ConfirmOptions {
  title: string;
  message: string;
  titleAccent?: string;
  detail?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'default';
}

/**
 * Narrow an unknown component instance to the optional content contract.
 * Every member is optional, so the cast asserts no capability - the frame
 * still typeof-checks each method before calling it.
 */
export function asDialogContent<TData = unknown, TPayload = unknown>(
  instance: unknown
): DialogContent<TData, TPayload> | null {
  return typeof instance === 'object' && instance !== null
    ? (instance as DialogContent<TData, TPayload>)
    : null;
}

/** The one place a content ComponentRef is unwrapped for the footer. */
export function contentOf<TData = unknown, TPayload = unknown>(
  ref: ComponentRef<unknown> | null
): DialogContent<TData, TPayload> | null {
  return ref ? asDialogContent<TData, TPayload>(ref.instance) : null;
}
